import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Plus, Trash2, Edit2, Save, X, BarChart3, ExternalLink } from "lucide-react";
import FunnelView from "@/components/admin/FunnelView";
import BannersManager from "@/components/admin/BannersManager";

const TABS = [
  { id: "offers", label: "Offers" },
  { id: "links", label: "Quick Links" },
  { id: "banners", label: "Banners" },
  { id: "funnel", label: "Funnel" },
  { id: "traffic", label: "Traffic" },
  { id: "analytics", label: "Analytics" },
];

const OFFER_BLANK = {
  bookmaker: "",
  title: "",
  description: "",
  tracking_url: "",
  badge: "",
  terms: "18+ New Customers Only. T&Cs Apply. BeGambleAware.org",
  accent_color: "#00E676",
  is_active: true,
  sort_order: 0,
};
const LINK_BLANK = { label: "", url: "", icon: "link", is_active: true, sort_order: 0 };

export default function AdminPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("offers");
  const [offers, setOffers] = useState([]);
  const [links, setLinks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [bannerAnalytics, setBannerAnalytics] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [editingLink, setEditingLink] = useState(null);

  const loadOffers = () => api.get("/admin/offers").then((r) => setOffers(r.data));
  const loadLinks = () => api.get("/admin/links").then((r) => setLinks(r.data));
  const loadAnalytics = () => api.get("/admin/analytics").then((r) => setAnalytics(r.data));
  const loadBannerAnalytics = () =>
    api.get("/admin/banner-analytics").then((r) => setBannerAnalytics(r.data));
  const loadTraffic = () => api.get("/admin/traffic").then((r) => setTraffic(r.data));

  useEffect(() => {
    if (user) {
      loadOffers();
      loadLinks();
      loadAnalytics();
      loadBannerAnalytics();
      loadTraffic();
    }
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const saveOffer = async () => {
    const data = { ...editingOffer };
    delete data.id; delete data.clicks; delete data.created_at;
    if (editingOffer.id) await api.put(`/admin/offers/${editingOffer.id}`, data);
    else await api.post(`/admin/offers`, data);
    setEditingOffer(null);
    loadOffers();
  };
  const deleteOffer = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    await api.delete(`/admin/offers/${id}`);
    loadOffers();
  };
  const saveLink = async () => {
    const data = { ...editingLink };
    delete data.id; delete data.clicks; delete data.created_at;
    if (editingLink.id) await api.put(`/admin/links/${editingLink.id}`, data);
    else await api.post(`/admin/links`, data);
    setEditingLink(null);
    loadLinks();
  };
  const deleteLink = async (id) => {
    if (!window.confirm("Delete this link?")) return;
    await api.delete(`/admin/links/${id}`);
    loadLinks();
  };

  return (
    <div data-testid="admin-page" className="min-h-screen max-w-5xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <header className="flex flex-wrap gap-4 items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-black">Admin Console</h1>
          <p className="font-mono text-[10px] uppercase tracking-wider mt-1" style={{ color: "var(--sb-text-muted)" }}>
            {user.email}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            <a data-testid="admin-view-site-link" href="/" target="_blank" rel="noreferrer" className="sb-btn-outline px-4 py-2 text-xs uppercase inline-flex items-center gap-2">
              <ExternalLink size={14} /> View Site
            </a>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/admin/login"); }}
              className="sb-btn-outline px-4 py-2 text-xs uppercase inline-flex items-center gap-2"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 border-b" style={{ borderColor: "var(--sb-border)" }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className="px-4 py-2 font-mono text-xs uppercase tracking-wider"
            style={{
              color: tab === t.id ? "var(--sb-primary)" : "var(--sb-text-muted)",
              borderBottom: tab === t.id ? "2px solid var(--sb-primary)" : "2px solid transparent",
              marginBottom: "-1px",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "offers" && (
        <section className="space-y-3">
          <div className="flex justify-end">
            <button
              data-testid="add-offer-btn"
              onClick={() => setEditingOffer({ ...OFFER_BLANK, sort_order: offers.length })}
              className="sb-btn-primary px-4 py-2 text-xs uppercase inline-flex items-center gap-2"
            >
              <Plus size={14} /> Add Offer
            </button>
          </div>

          {editingOffer ? (
            <OfferEditor offer={editingOffer} setOffer={setEditingOffer} onSave={saveOffer} onCancel={() => setEditingOffer(null)} />
          ) : null}

          <div className="grid gap-2">
            {offers.map((o) => (
              <div key={o.id} className="sb-card rounded-lg p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] uppercase" style={{ color: "var(--sb-text-muted)" }}>
                    {o.bookmaker} · #{o.sort_order} · {o.is_active ? "ACTIVE" : "HIDDEN"}
                  </p>
                  <p className="font-display font-bold truncate">{o.title}</p>
                </div>
                <span className="font-mono text-xs" style={{ color: "var(--sb-primary)" }}>{o.clicks} clicks</span>
                <button data-testid={`edit-offer-${o.id}`} onClick={() => setEditingOffer(o)} className="sb-btn-outline p-2"><Edit2 size={14} /></button>
                <button data-testid={`delete-offer-${o.id}`} onClick={() => deleteOffer(o.id)} className="sb-btn-outline p-2" style={{ borderColor: "rgba(255,59,48,0.4)", color: "#FF6B61" }}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "links" && (
        <section className="space-y-3">
          <div className="flex justify-end">
            <button
              data-testid="add-link-btn"
              onClick={() => setEditingLink({ ...LINK_BLANK, sort_order: links.length })}
              className="sb-btn-primary px-4 py-2 text-xs uppercase inline-flex items-center gap-2"
            >
              <Plus size={14} /> Add Link
            </button>
          </div>
          {editingLink ? (
            <LinkEditor link={editingLink} setLink={setEditingLink} onSave={saveLink} onCancel={() => setEditingLink(null)} />
          ) : null}
          <div className="grid gap-2">
            {links.map((l) => (
              <div key={l.id} className="sb-card rounded-lg p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-[10px] uppercase" style={{ color: "var(--sb-text-muted)" }}>
                    {l.icon} · #{l.sort_order} · {l.is_active ? "ACTIVE" : "HIDDEN"}
                  </p>
                  <p className="font-display font-bold truncate">{l.label}</p>
                  <p className="text-xs truncate" style={{ color: "var(--sb-text-secondary)" }}>{l.url}</p>
                </div>
                <span className="font-mono text-xs" style={{ color: "var(--sb-primary)" }}>{l.clicks} clicks</span>
                <button data-testid={`edit-link-${l.id}`} onClick={() => setEditingLink(l)} className="sb-btn-outline p-2"><Edit2 size={14} /></button>
                <button data-testid={`delete-link-${l.id}`} onClick={() => deleteLink(l.id)} className="sb-btn-outline p-2" style={{ borderColor: "rgba(255,59,48,0.4)", color: "#FF6B61" }}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "banners" && (
        <section className="space-y-3">
          <BannersManager />
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-bold">Affiliate Banner Performance</h2>
              <p className="font-mono text-[10px] uppercase tracking-wider mt-1" style={{ color: "var(--sb-text-muted)" }}>
                In-house tracking · independent of network pixels
              </p>
            </div>
            <button
              data-testid="refresh-banners-btn"
              onClick={loadBannerAnalytics}
              className="sb-btn-outline px-3 py-2 text-xs uppercase inline-flex items-center gap-2"
            >
              <BarChart3 size={14} /> Refresh
            </button>
          </div>

          {!bannerAnalytics ? (
            <p className="font-mono text-xs" style={{ color: "var(--sb-text-muted)" }}>Loading…</p>
          ) : bannerAnalytics.banners.length === 0 ? (
            <div className="sb-card rounded-lg p-6 text-center">
              <p className="font-mono text-xs" style={{ color: "var(--sb-text-muted)" }}>
                No banner events recorded yet. Once visitors land on the home page, impressions and clicks for each Matchbook banner will appear here.
              </p>
            </div>
          ) : (
            <div className="sb-card rounded-lg overflow-hidden">
              <div
                className="grid grid-cols-12 px-4 py-2 font-mono text-[10px] uppercase tracking-wider"
                style={{ color: "var(--sb-text-muted)", borderBottom: "1px solid var(--sb-border)" }}
              >
                <div className="col-span-5">Banner ID</div>
                <div className="col-span-2 text-right">Impressions</div>
                <div className="col-span-2 text-right">Clicks</div>
                <div className="col-span-3 text-right">CTR</div>
              </div>
              {bannerAnalytics.banners.map((b) => (
                <div
                  key={b.banner_id}
                  data-testid={`banner-row-${b.banner_id}`}
                  className="grid grid-cols-12 px-4 py-3 items-center"
                  style={{ borderBottom: "1px solid var(--sb-border)" }}
                >
                  <div className="col-span-5 font-mono text-xs truncate">{b.banner_id}</div>
                  <div className="col-span-2 text-right font-mono text-sm">{b.impressions}</div>
                  <div className="col-span-2 text-right font-mono text-sm" style={{ color: "var(--sb-primary)" }}>
                    {b.clicks}
                  </div>
                  <div className="col-span-3 text-right font-mono text-sm font-bold">
                    {b.ctr.toFixed(2)}%
                  </div>
                </div>
              ))}
              <div
                className="px-4 py-2 font-mono text-[10px] uppercase tracking-wider"
                style={{ color: "var(--sb-text-muted)" }}
              >
                Total events tracked: {bannerAnalytics.total_events}
              </div>
            </div>
          )}
        </section>
      )}

      {tab === "funnel" && <FunnelView />}

      {tab === "traffic" && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-bold">Site Traffic</h2>
              <p className="font-mono text-[10px] uppercase tracking-wider mt-1" style={{ color: "var(--sb-text-muted)" }}>
                GDPR-safe · IP+UA hashed · no raw identifiers stored
              </p>
            </div>
            <button
              data-testid="refresh-traffic-btn"
              onClick={loadTraffic}
              className="sb-btn-outline px-3 py-2 text-xs uppercase inline-flex items-center gap-2"
            >
              <BarChart3 size={14} /> Refresh
            </button>
          </div>

          {!traffic ? (
            <p className="font-mono text-xs" style={{ color: "var(--sb-text-muted)" }}>Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Views Today" value={traffic.totals.today_views} />
                <Stat label="Unique Today" value={traffic.totals.today_unique} />
                <Stat label="Views (7d)" value={traffic.totals.last_7d_views} />
                <Stat label="Lifetime Views" value={traffic.totals.lifetime_views} />
              </div>

              <div className="sb-card rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--sb-text-muted)" }}>
                    // Last 14 Days
                  </h3>
                  <span className="font-mono text-[10px]" style={{ color: "var(--sb-text-muted)" }}>
                    Views (bar) · Unique (dot)
                  </span>
                </div>
                <TrafficChart daily={traffic.daily} />
              </div>

              <div
                className="font-mono text-[10px] uppercase tracking-wider"
                style={{ color: "var(--sb-text-muted)" }}
              >
                Lifetime unique visitors: {traffic.totals.lifetime_unique}
              </div>
            </>
          )}
        </section>
      )}

      {tab === "analytics" && analytics && (
        <section className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="Offer Clicks" value={analytics.totals.offer_clicks} />
            <Stat label="Link Clicks" value={analytics.totals.link_clicks} />
            <Stat label="Total Events" value={analytics.totals.events} />
          </div>
          <div className="sb-card rounded-lg p-5">
            <h3 className="font-mono text-[10px] uppercase tracking-wider mb-3" style={{ color: "var(--sb-text-muted)" }}>// Top Offers</h3>
            <div className="space-y-2">
              {analytics.offers.map((o) => (
                <div key={o.id} className="flex items-center gap-3">
                  <span className="font-mono text-xs flex-1 truncate">{o.bookmaker} — {o.title}</span>
                  <span className="font-mono text-sm" style={{ color: "var(--sb-primary)" }}>{o.clicks}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="sb-card rounded-lg p-5">
            <h3 className="font-mono text-[10px] uppercase tracking-wider mb-3" style={{ color: "var(--sb-text-muted)" }}>// Top Links</h3>
            <div className="space-y-2">
              {analytics.links.map((l) => (
                <div key={l.id} className="flex items-center gap-3">
                  <span className="font-mono text-xs flex-1 truncate">{l.label}</span>
                  <span className="font-mono text-sm" style={{ color: "var(--sb-primary)" }}>{l.clicks}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="sb-card rounded-lg p-5">
      <p className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--sb-text-muted)" }}>{label}</p>
      <p className="font-display text-3xl font-black mt-1" style={{ color: "var(--sb-primary)" }}>{value}</p>
    </div>
  );
}

function TrafficChart({ daily }) {
  const maxViews = Math.max(1, ...daily.map((d) => d.views));
  const fmt = (day) => {
    const d = new Date(day + "T00:00:00Z");
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" });
  };
  return (
    <div data-testid="traffic-chart" className="flex items-end justify-between gap-1 h-44">
      {daily.map((d) => {
        const h = (d.views / maxViews) * 100;
        const uniquePct = d.views ? (d.unique / d.views) * 100 : 0;
        return (
          <div key={d.day} className="flex-1 flex flex-col items-center gap-2 min-w-0">
            <div className="relative w-full flex items-end justify-center" style={{ height: "140px" }}>
              <div
                title={`${fmt(d.day)} · ${d.views} views · ${d.unique} unique`}
                className="w-full rounded-t-sm transition-all duration-300 relative"
                style={{
                  height: `${Math.max(2, h)}%`,
                  background: "linear-gradient(180deg, rgba(61,126,255,0.95) 0%, rgba(61,126,255,0.35) 100%)",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18)",
                }}
              >
                {d.unique > 0 && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 rounded-full"
                    style={{
                      bottom: `${uniquePct}%`,
                      width: 6,
                      height: 6,
                      background: "#00E676",
                      boxShadow: "0 0 8px rgba(0,230,118,0.7)",
                      transform: "translate(-50%, 50%)",
                    }}
                  />
                )}
              </div>
            </div>
            <span className="font-mono text-[9px] tracking-wider whitespace-nowrap" style={{ color: "var(--sb-text-muted)" }}>
              {fmt(d.day).split(" ")[0]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  );
}

function OfferEditor({ offer, setOffer, onSave, onCancel }) {
  const u = (k, v) => setOffer({ ...offer, [k]: v });
  return (
    <div data-testid="offer-editor" className="sb-card rounded-lg p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Bookmaker"><input className="sb-input" value={offer.bookmaker} onChange={(e) => u("bookmaker", e.target.value)} /></Field>
      <Field label="Badge (optional)"><input className="sb-input" value={offer.badge} onChange={(e) => u("badge", e.target.value)} /></Field>
      <Field label="Offer Title"><input className="sb-input" value={offer.title} onChange={(e) => u("title", e.target.value)} /></Field>
      <Field label="Accent color"><input className="sb-input" value={offer.accent_color} onChange={(e) => u("accent_color", e.target.value)} /></Field>
      <div className="sm:col-span-2">
        <Field label="Tracking URL"><input className="sb-input" value={offer.tracking_url} onChange={(e) => u("tracking_url", e.target.value)} /></Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Description"><input className="sb-input" value={offer.description} onChange={(e) => u("description", e.target.value)} /></Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Terms"><input className="sb-input" value={offer.terms} onChange={(e) => u("terms", e.target.value)} /></Field>
      </div>
      <Field label="Sort Order"><input type="number" className="sb-input" value={offer.sort_order} onChange={(e) => u("sort_order", parseInt(e.target.value) || 0)} /></Field>
      <Field label="Active">
        <select className="sb-input" value={offer.is_active ? "1" : "0"} onChange={(e) => u("is_active", e.target.value === "1")}>
          <option value="1">Active</option><option value="0">Hidden</option>
        </select>
      </Field>
      <div className="sm:col-span-2 flex gap-2 mt-2">
        <button data-testid="save-offer-btn" onClick={onSave} className="sb-btn-primary px-5 py-2 text-xs uppercase inline-flex items-center gap-2"><Save size={14} /> Save</button>
        <button onClick={onCancel} className="sb-btn-outline px-5 py-2 text-xs uppercase inline-flex items-center gap-2"><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}

function LinkEditor({ link, setLink, onSave, onCancel }) {
  const u = (k, v) => setLink({ ...link, [k]: v });
  return (
    <div data-testid="link-editor" className="sb-card rounded-lg p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Label"><input className="sb-input" value={link.label} onChange={(e) => u("label", e.target.value)} /></Field>
      <Field label="Icon (line-chart, trending-up, twitter, send, mail, link, message, globe, youtube, instagram)"><input className="sb-input" value={link.icon} onChange={(e) => u("icon", e.target.value)} /></Field>
      <div className="sm:col-span-2"><Field label="URL"><input className="sb-input" value={link.url} onChange={(e) => u("url", e.target.value)} /></Field></div>
      <Field label="Sort Order"><input type="number" className="sb-input" value={link.sort_order} onChange={(e) => u("sort_order", parseInt(e.target.value) || 0)} /></Field>
      <Field label="Active">
        <select className="sb-input" value={link.is_active ? "1" : "0"} onChange={(e) => u("is_active", e.target.value === "1")}>
          <option value="1">Active</option><option value="0">Hidden</option>
        </select>
      </Field>
      <div className="sm:col-span-2 flex gap-2 mt-2">
        <button data-testid="save-link-btn" onClick={onSave} className="sb-btn-primary px-5 py-2 text-xs uppercase inline-flex items-center gap-2"><Save size={14} /> Save</button>
        <button onClick={onCancel} className="sb-btn-outline px-5 py-2 text-xs uppercase inline-flex items-center gap-2"><X size={14} /> Cancel</button>
      </div>
    </div>
  );
}
